"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useMemo, useState, useRef, useEffect, type MouseEvent as ReactMouseEvent } from "react";
import { useTranslations } from "next-intl";
import { GruvWordmark } from "@project-x/ui-kit";
import { hasActiveFilters } from "@/utils/eventsFilters";
import { useGruvShell } from "./GruvShell";
import { useAuth } from "@/app/auth/AuthSessionProvider";
import { NotificationBell } from "./NotificationBell";
import { NotificationGlyphIcon } from "./NotificationGlyphIcon";

const shouldLogDebug = process.env.NODE_ENV !== "production";


const PRIMARY_NAV = [
  { href: "/events", labelKey: "header.primaryNav.events" },
  { href: "/account/tickets", labelKey: "header.primaryNav.myTickets" },
  { href: "/swap-market/", labelKey: "header.primaryNav.swapMarket" },
  { href: "/friends", labelKey: "header.primaryNav.friends" },
];

export function GruvHeader() {
  const t = useTranslations("layout");
  const pathname = usePathname();
  const router = useRouter();
  const { openSearch, isSearchOpen, filters, resetFilters } = useGruvShell();
  const { isAuthenticated, isLoading } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const mobileNavToggleRef = useRef<HTMLButtonElement>(null);

  const searchLabel = isSearchOpen ? t("header.search.close") : t("header.search.open");
  const authButtonLabel = isAuthenticated ? t("header.auth.profile") : t("header.auth.login");
  const authButtonHref = useMemo(() => (isAuthenticated ? "/account/profile" : "/auth/login"), [isAuthenticated]);

  const handleAuthClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (!isAuthenticated && pathname?.startsWith("/events/")) {
      event.preventDefault();
      router.push(headerLoginHref(pathname, window.location.search));
    }
  };

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        mobileNavOpen &&
        mobileNavRef.current &&
        !mobileNavRef.current.contains(target) &&
        mobileNavToggleRef.current &&
        !mobileNavToggleRef.current.contains(target)
      ) {
        setMobileNavOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [mobileNavOpen]);

  const handleSearchClick = () => {
    if (shouldLogDebug) {
      console.debug(`[GRUV][Header] Search trigger clicked (isSearchOpen=${isSearchOpen})`);
    }
    openSearch();
  };

  const showClearFilters = pathname === "/events" && hasActiveFilters(filters);

  const handleClearFiltersClick = () => {
    if (shouldLogDebug) {
      console.debug("[GRUV][Header] Clear filters clicked");
    }
    resetFilters();
  };

  const handleNavClick = () => {
    setMobileNavOpen(false);
  };

  return (
    <header className="gruv-header">
      <div className="gruv-header__inner">
        <Link href="/events" className="gruv-header__brand" aria-label={t("header.aria.brandHome")}>
          <GruvWordmark className="gruv-header__logo" height={28} priority />
          <span className="gruv-header__tagline">{t("header.brandTagline")}</span>
        </Link>

        <nav className="gruv-header__nav" aria-label={t("header.aria.primaryNav")}>
          {PRIMARY_NAV.filter(item => {
            if (item.href === "/events") return true;
            return isAuthenticated;
          }).map((item) => (
            <Link key={item.href} href={item.href} className="gruv-header__nav-item">
              {t(item.labelKey)}
            </Link>
          ))}
        </nav>

        <div className="gruv-header__actions">
          <button
            type="button"
            ref={mobileNavToggleRef}
            className="gruv-header__mobile-nav-toggle"
            onClick={() => setMobileNavOpen(open => !open)}
            aria-label={t("header.aria.mobileNavToggle")}
            aria-expanded={mobileNavOpen}
          >
            <span aria-hidden="true">{mobileNavOpen ? "✕" : "☰"}</span>
          </button>
          <button type="button" className="gruv-header__search" onClick={handleSearchClick}>
            <span className="sr-only">{searchLabel}</span>
            <span aria-hidden="true" className="gruv-header__search-label">
              {t("header.search.label")}
            </span>
          </button>
          {showClearFilters ? (
            <button
              type="button"
              className="gruv-header__clear-filters"
              onClick={handleClearFiltersClick}
              aria-label={t("header.search.clearFilters")}
            >
              <span aria-hidden="true">✕</span>
            </button>
          ) : null}

          {/* Notification Bell - only shows when logged in */}
          <NotificationBell />

          {!isLoading && (
            <Link
              href={authButtonHref}
              onClick={handleAuthClick}
              className="gruv-header__cta gruv-header__cta--ghost gruv-header__cta--profile"
              aria-label={authButtonLabel}
            >
              <span aria-hidden="true" className="gruv-header__cta-icon">
                <NotificationGlyphIcon name="person" size={20} />
              </span>
              <span className="gruv-header__cta-text">{authButtonLabel}</span>
            </Link>
          )}
        </div>
      </div>
      {/* Mobile Navigation Menu */}
      {mobileNavOpen && (
        <nav className="gruv-header__mobile-nav" ref={mobileNavRef} aria-label={t("header.aria.mobileNav")}>
          {PRIMARY_NAV.filter(item => {
            if (item.href === "/events") return true;
            return isAuthenticated;
          }).map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="gruv-header__mobile-nav-item"
              onClick={handleNavClick}
            >
              {t(item.labelKey)}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

/** Builds an event-preserving header login URL without reading browser state during render. */
export function headerLoginHref(pathname: string, search: string): string {
  return `/auth/login?returnTo=${encodeURIComponent(`${pathname}${search}`)}`;
}
